import "../dashboardPage/dashboard.page.css";
import "../portal.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Skeleton } from "antd";
import { Navbar } from "@src/components/navbar";
import { getCustomerPortalInvoicesQuery } from "@src/util/query/portal.query";
import { downloadCsv } from "@src/util/portal/download";

// Invoices - everything the provider has sent (drafts never show): outstanding,
// overdue (past the due date from the provider's payment terms) and paid to date,
// the list, and a statement download. An invoice opens as the printable document.

export const INVOICE_STATE = {
    outstanding: { label: "Outstanding", color: "#0091ae", bg: "#e5f5f8" },
    overdue:     { label: "Overdue",     color: "#dc2626", bg: "#fef2f2" },
    paid:        { label: "Paid",        color: "#16a34a", bg: "#f0fdf4" },
};

export const formatMoney = (amount, currency = "GBP") => {
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount || 0);
    } catch (e) {
        return `${currency} ${(amount || 0).toFixed(2)}`;
    }
};

// {GBP: 10, EUR: 5} -> "£10.00 + €5.00" - currencies are never added together
const formatTotals = (byCurrency) => {
    const entries = Object.entries(byCurrency || {}).filter(([, v]) => v);
    return entries.length ? entries.map(([c, v]) => formatMoney(v, c)).join(" + ") : formatMoney(0);
};

const FILTERS = [
    { key: "all", label: "All" },
    { key: "outstanding", label: "Outstanding" },
    { key: "overdue", label: "Overdue" },
    { key: "paid", label: "Paid" },
];

export const Invoices = () => {
    const navigate = useNavigate();
    const [customerDetail, setCustomerDetail] = useState(null);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const { data, loading } = useQuery(getCustomerPortalInvoicesQuery, { fetchPolicy: "cache-and-network" });
    const result = data?.getCustomerPortalInvoices?.response;
    const invoices = useMemo(() => result?.invoices || [], [result]);
    const totals = result?.totals || {};
    const isLoading = loading && !result;

    const visible = invoices.filter((inv) => {
        // overdue invoices are outstanding too
        if (filter === "outstanding" && inv.state === "paid") return false;
        if (filter === "overdue" && inv.state !== "overdue") return false;
        if (filter === "paid" && inv.state !== "paid") return false;
        const q = search.trim().toLowerCase();
        return !q || [inv.invoiceNo, inv.poNo].some((v) => String(v || "").toLowerCase().includes(q));
    });

    const overdueCount = invoices.filter((i) => i.state === "overdue").length;
    const provider = customerDetail?.tenantDetail?.legalname;

    const exportStatement = () => {
        downloadCsv(
            "statement-of-account.csv",
            ["Invoice", "Tax date", "Period from", "Period to", "Due", "PO", "Currency", "Net", "VAT", "Total", "Status"],
            invoices.map((i) => [i.invoiceNo, i.taxDate, i.transactionFrom, i.transactionTo, i.dueDate || "", i.poNo || "", i.currency, i.netAmount, i.vatAmount, i.totalAmount, INVOICE_STATE[i.state]?.label])
        );
    };

    return (
        <div className="cdb-page">
            <Navbar customerDetail={customerDetail} />

            <div className="cdb-body">
                <header className="cp-page-head">
                    <div>
                        <h1 className="cdb-title">Invoices</h1>
                        <div className="cp-page-sub">
                            Invoices {provider ? `from ${provider}` : "for your account"}{result?.paymentTerms ? ` · payment terms ${result.paymentTerms}` : ""}.
                        </div>
                    </div>
                    <div className="cdb-header-actions">
                        <button type="button" className="cdb-btn" onClick={exportStatement} disabled={!invoices.length}>Download statement (Excel)</button>
                    </div>
                </header>

                <div className="cdb-kpis cp-kpis-3">
                    <button type="button" className="cdb-kpi cdb-kpi-clickable" onClick={() => setFilter("outstanding")}>
                        <span className="cdb-kpi-label">Outstanding</span>
                        {isLoading ? <Skeleton.Input active size="small" /> : <span className="cdb-kpi-value">{formatTotals(totals.outstanding)}</span>}
                        <span className="cdb-kpi-sub">Sent and not yet paid</span>
                    </button>
                    <button type="button" className={`cdb-kpi cdb-kpi-clickable${overdueCount ? " cp-tile-alert" : ""}`} onClick={() => setFilter("overdue")}>
                        <span className="cdb-kpi-label">Overdue</span>
                        {isLoading ? <Skeleton.Input active size="small" /> : <span className="cdb-kpi-value">{formatTotals(totals.overdue)}</span>}
                        <span className="cdb-kpi-sub">{overdueCount ? `${overdueCount} invoice${overdueCount === 1 ? "" : "s"} past the due date` : "Nothing overdue"}</span>
                    </button>
                    <button type="button" className="cdb-kpi cdb-kpi-clickable" onClick={() => setFilter("paid")}>
                        <span className="cdb-kpi-label">Paid to date</span>
                        {isLoading ? <Skeleton.Input active size="small" /> : <span className="cdb-kpi-value">{formatTotals(totals.paid)}</span>}
                        <span className="cdb-kpi-sub">All paid invoices</span>
                    </button>
                </div>

                <section className="cdb-card">
                    <div className="cdb-card-head cp-card-head-wrap">
                        <div className="cdb-segmented" role="tablist">
                            {FILTERS.map((f) => (
                                <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={filter === f.key ? "active" : ""} onClick={() => setFilter(f.key)}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <input className="generic-input-control cp-search" placeholder="Search invoice or PO number" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    {isLoading ? <div className="cdb-card-body"><Skeleton active paragraph={{ rows: 5 }} /></div> : visible.length === 0 ? (
                        <div className="cdb-empty">{invoices.length ? "No invoices match." : "No invoices yet. They will appear here once your provider sends them."}</div>
                    ) : (
                        <div className="cp-table-wrap">
                            <table className="cp-table">
                                <thead>
                                    <tr><th>Invoice</th><th>Tax date</th><th>Period</th><th>Due</th><th>PO</th><th className="cp-num">Total</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {visible.map((inv) => {
                                        const s = INVOICE_STATE[inv.state] || INVOICE_STATE.outstanding;
                                        return (
                                            <tr key={inv._id} className="cp-row-click" onClick={() => navigate(`/customer/invoices/${inv._id}`)}>
                                                <td><b>{inv.invoiceNo}</b></td>
                                                <td>{inv.taxDate}</td>
                                                <td>{inv.transactionFrom} – {inv.transactionTo}</td>
                                                <td>{inv.dueDate || "—"}</td>
                                                <td>{inv.poNo || "—"}</td>
                                                <td className="cp-num"><b>{formatMoney(inv.totalAmount, inv.currency)}</b></td>
                                                <td><span className="cdb-pill" style={{ color: s.color, borderColor: s.color, background: s.bg }}>{s.label}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};
